/**
 * HVN: Property Inquiry Form block — editor component.
 *
 * Inspector UX only. The live preview is ServerSideRender of the reused Contact
 * Agent form. No inquiry business logic lives here.
 *
 * Normal users pick properties/agents by search — never by ID.
 * Manual IDs live under Advanced for developers only.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	TextControl,
	RangeControl,
	Notice,
} from '@wordpress/components';

import BlockPreview from '../shared/BlockPreview';
import EntitySearchControl from '../shared/EntitySearchControl';

const PROPERTY_SOURCE_OPTIONS = [
	{ label: __( 'Current Property', 'havenlytics' ), value: 'current' },
	{ label: __( 'Selected Property', 'havenlytics' ), value: 'selected' },
	{ label: __( 'Multiple Properties', 'havenlytics' ), value: 'multiple' },
];

const AGENT_SOURCE_OPTIONS = [
	{ label: __( 'Property assigned agent(s)', 'havenlytics' ), value: 'auto' },
	{ label: __( 'Current agent (agent pages)', 'havenlytics' ), value: 'current' },
	{ label: __( 'Selected Agent', 'havenlytics' ), value: 'selected' },
	{ label: __( 'Multiple Agents', 'havenlytics' ), value: 'multiple' },
	{ label: __( 'No specific agent', 'havenlytics' ), value: 'none' },
];

const LAYOUT_OPTIONS = [
	{ label: __( 'Vertical', 'havenlytics' ), value: 'vertical' },
	{ label: __( 'Horizontal', 'havenlytics' ), value: 'horizontal' },
	{ label: __( '2 Column', 'havenlytics' ), value: 'two-column' },
	{ label: __( 'Compact', 'havenlytics' ), value: 'compact' },
	{ label: __( 'Card', 'havenlytics' ), value: 'card' },
	{ label: __( 'Minimal', 'havenlytics' ), value: 'minimal' },
	{ label: __( 'Floating Card', 'havenlytics' ), value: 'floating' },
];

const BUTTON_WIDTH_OPTIONS = [
	{ label: __( 'Auto', 'havenlytics' ), value: 'auto' },
	{ label: __( 'Full width', 'havenlytics' ), value: 'full' },
];

const BUTTON_ALIGN_OPTIONS = [
	{ label: __( 'Left', 'havenlytics' ), value: 'left' },
	{ label: __( 'Center', 'havenlytics' ), value: 'center' },
	{ label: __( 'Right', 'havenlytics' ), value: 'right' },
];

const BUTTON_ICON_OPTIONS = [
	{ label: __( 'None', 'havenlytics' ), value: 'none' },
	{ label: __( 'Send', 'havenlytics' ), value: 'send' },
	{ label: __( 'Envelope', 'havenlytics' ), value: 'envelope' },
	{ label: __( 'Check', 'havenlytics' ), value: 'check' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		propertySource,
		propertyId,
		propertyIds,
		fallbackPropertyId,
		agentSource,
		agentId,
		agentIds,
		showTitle,
		formTitle,
		showDescription,
		formDescription,
		showPropertyImage,
		showPropertyPrice,
		showPropertyAddress,
		showAgentCard,
		layout,
		formWidth,
		showName,
		showEmail,
		showPhone,
		showMessage,
		showConsent,
		consentText,
		buttonText,
		buttonWidth,
		buttonAlign,
		buttonIcon,
		showLoadingState,
		showSuccessState,
		successMessage,
		successRedirectUrl,
	} = attributes;

	const blockProps = useBlockProps();
	const ids = Array.isArray( propertyIds ) ? propertyIds : [];
	const aIds = Array.isArray( agentIds ) ? agentIds : [];

	const propertySourceOptions =
		propertySource === 'manual'
			? [
					...PROPERTY_SOURCE_OPTIONS,
					{
						label: __( 'Manual Property ID (Advanced)', 'havenlytics' ),
						value: 'manual',
					},
			  ]
			: PROPERTY_SOURCE_OPTIONS;

	const agentSourceOptions =
		agentSource === 'manual'
			? [
					...AGENT_SOURCE_OPTIONS,
					{
						label: __( 'Manual Agent ID (Advanced)', 'havenlytics' ),
						value: 'manual',
					},
			  ]
			: AGENT_SOURCE_OPTIONS;

	const missingProperty =
		( propertySource === 'selected' && ! propertyId ) ||
		( propertySource === 'manual' && ! propertyId ) ||
		( propertySource === 'multiple' && ids.length === 0 ) ||
		( propertySource === 'current' && ! fallbackPropertyId );

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Content', 'havenlytics' ) }
					initialOpen={ true }
				>
					<p className="components-base-control__help">
						{ __(
							'Choose which property and agent this form is about. Visitors never see IDs.',
							'havenlytics'
						) }
					</p>
					<SelectControl
						label={ __( 'Property source', 'havenlytics' ) }
						value={ propertySource }
						options={ propertySourceOptions }
						onChange={ ( value ) =>
							setAttributes( { propertySource: value } )
						}
						__nextHasNoMarginBottom
					/>

					{ propertySource === 'selected' && (
						<EntitySearchControl
							postType="hvnly_property"
							label={ __( 'Property', 'havenlytics' ) }
							value={ propertyId }
							onChange={ ( value ) =>
								setAttributes( { propertyId: value || 0 } )
							}
							help={ __(
								'Browse published properties. Search is optional.',
								'havenlytics'
							) }
						/>
					) }

					{ propertySource === 'multiple' && (
						<EntitySearchControl
							postType="hvnly_property"
							label={ __( 'Properties', 'havenlytics' ) }
							multiple
							value={ ids }
							onChange={ ( value ) =>
								setAttributes( { propertyIds: value } )
							}
							help={ __(
								'Visitors pick one title; the existing property_id is submitted.',
								'havenlytics'
							) }
						/>
					) }

					{ propertySource === 'current' && (
						<EntitySearchControl
							postType="hvnly_property"
							label={ __( 'Fallback property', 'havenlytics' ) }
							value={ fallbackPropertyId }
							onChange={ ( value ) =>
								setAttributes( {
									fallbackPropertyId: value || 0,
								} )
							}
							help={ __(
								'Used when this page is not a single property.',
								'havenlytics'
							) }
						/>
					) }

					{ propertySource === 'manual' && (
						<Notice status="info" isDismissible={ false }>
							{ __(
								'Manual Property ID is enabled under Advanced.',
								'havenlytics'
							) }
						</Notice>
					) }

					<SelectControl
						label={ __( 'Agent source', 'havenlytics' ) }
						value={ agentSource }
						options={ agentSourceOptions }
						onChange={ ( value ) =>
							setAttributes( { agentSource: value } )
						}
						help={ __(
							'Recipients are still validated by the Contact Agent system.',
							'havenlytics'
						) }
						__nextHasNoMarginBottom
					/>

					{ agentSource === 'selected' && (
						<EntitySearchControl
							postType="hvnly_agent"
							label={ __( 'Agent', 'havenlytics' ) }
							value={ agentId }
							onChange={ ( value ) =>
								setAttributes( { agentId: value || 0 } )
							}
							help={ __(
								'Browse published agents. Search is optional.',
								'havenlytics'
							) }
						/>
					) }

					{ agentSource === 'multiple' && (
						<EntitySearchControl
							postType="hvnly_agent"
							label={ __( 'Agents', 'havenlytics' ) }
							multiple
							value={ aIds }
							onChange={ ( value ) =>
								setAttributes( { agentIds: value } )
							}
							help={ __(
								'Visitors choose who receives the inquiry.',
								'havenlytics'
							) }
						/>
					) }

					{ agentSource === 'manual' && (
						<Notice status="info" isDismissible={ false }>
							{ __(
								'Manual Agent ID is enabled under Advanced.',
								'havenlytics'
							) }
						</Notice>
					) }
				</PanelBody>

				<PanelBody title={ __( 'Header & preview', 'havenlytics' ) } initialOpen={ false }>
					<ToggleControl
						label={ __( 'Show title', 'havenlytics' ) }
						checked={ !! showTitle }
						onChange={ ( value ) => setAttributes( { showTitle: value } ) }
						__nextHasNoMarginBottom
					/>
					{ showTitle && (
						<TextControl
							label={ __( 'Title', 'havenlytics' ) }
							value={ formTitle }
							onChange={ ( value ) => setAttributes( { formTitle: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
					<ToggleControl
						label={ __( 'Show description', 'havenlytics' ) }
						checked={ !! showDescription }
						onChange={ ( value ) => setAttributes( { showDescription: value } ) }
						__nextHasNoMarginBottom
					/>
					{ showDescription && (
						<TextControl
							label={ __( 'Description', 'havenlytics' ) }
							value={ formDescription }
							onChange={ ( value ) => setAttributes( { formDescription: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
					<ToggleControl
						label={ __( 'Show property image', 'havenlytics' ) }
						checked={ !! showPropertyImage }
						onChange={ ( value ) => setAttributes( { showPropertyImage: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show property price', 'havenlytics' ) }
						checked={ !! showPropertyPrice }
						onChange={ ( value ) => setAttributes( { showPropertyPrice: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show property address', 'havenlytics' ) }
						checked={ !! showPropertyAddress }
						onChange={ ( value ) => setAttributes( { showPropertyAddress: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show agent card', 'havenlytics' ) }
						checked={ !! showAgentCard }
						onChange={ ( value ) => setAttributes( { showAgentCard: value } ) }
						help={ __( 'Compact agent summary above the form.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Layout', 'havenlytics' ) } initialOpen={ false }>
					<SelectControl
						label={ __( 'Form layout', 'havenlytics' ) }
						value={ layout }
						options={ LAYOUT_OPTIONS }
						onChange={ ( value ) => setAttributes( { layout: value } ) }
						help={ __( 'Card and Floating wrap the form in a bordered surface.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Max width (px)', 'havenlytics' ) }
						value={ formWidth }
						min={ 0 }
						max={ 900 }
						onChange={ ( value ) => setAttributes( { formWidth: value } ) }
						help={ __( '0 keeps the form full width of its column.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Fields', 'havenlytics' ) } initialOpen={ false }>
					<p className="components-base-control__help">
						{ __( 'Name, email and message stay required by the inquiry system.', 'havenlytics' ) }
					</p>
					<ToggleControl
						label={ __( 'Show name', 'havenlytics' ) }
						checked={ showName !== false }
						onChange={ ( value ) => setAttributes( { showName: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show email', 'havenlytics' ) }
						checked={ showEmail !== false }
						onChange={ ( value ) => setAttributes( { showEmail: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show phone', 'havenlytics' ) }
						checked={ showPhone !== false }
						onChange={ ( value ) => setAttributes( { showPhone: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show message', 'havenlytics' ) }
						checked={ showMessage !== false }
						onChange={ ( value ) => setAttributes( { showMessage: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show consent checkbox', 'havenlytics' ) }
						checked={ !! showConsent }
						onChange={ ( value ) => setAttributes( { showConsent: value } ) }
						__nextHasNoMarginBottom
					/>
					{ showConsent && (
						<TextControl
							label={ __( 'Consent text', 'havenlytics' ) }
							value={ consentText }
							onChange={ ( value ) => setAttributes( { consentText: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>

				<PanelBody title={ __( 'Submit & success', 'havenlytics' ) } initialOpen={ false }>
					<TextControl
						label={ __( 'Button text', 'havenlytics' ) }
						value={ buttonText }
						placeholder={ __( 'Leave empty for default', 'havenlytics' ) }
						onChange={ ( value ) => setAttributes( { buttonText: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Button width', 'havenlytics' ) }
						value={ buttonWidth }
						options={ BUTTON_WIDTH_OPTIONS }
						onChange={ ( value ) => setAttributes( { buttonWidth: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Button alignment', 'havenlytics' ) }
						value={ buttonAlign }
						options={ BUTTON_ALIGN_OPTIONS }
						onChange={ ( value ) => setAttributes( { buttonAlign: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Button icon', 'havenlytics' ) }
						value={ buttonIcon }
						options={ BUTTON_ICON_OPTIONS }
						onChange={ ( value ) => setAttributes( { buttonIcon: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show loading state', 'havenlytics' ) }
						checked={ showLoadingState !== false }
						onChange={ ( value ) => setAttributes( { showLoadingState: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show success notice', 'havenlytics' ) }
						checked={ showSuccessState !== false }
						onChange={ ( value ) => setAttributes( { showSuccessState: value } ) }
						__nextHasNoMarginBottom
					/>
					<TextControl
						label={ __( 'Custom success message', 'havenlytics' ) }
						value={ successMessage }
						help={ __( 'Optional overlay on the existing success notice.', 'havenlytics' ) }
						onChange={ ( value ) => setAttributes( { successMessage: value } ) }
						__nextHasNoMarginBottom
					/>
					<TextControl
						type="url"
						label={ __( 'Redirect after success', 'havenlytics' ) }
						value={ successRedirectUrl }
						help={ __( 'Optional URL after the success notice appears.', 'havenlytics' ) }
						onChange={ ( value ) => setAttributes( { successRedirectUrl: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Advanced', 'havenlytics' ) }
					initialOpen={ propertySource === 'manual' || agentSource === 'manual' }
				>
					<p className="components-base-control__help">
						{ __(
							'Developer fallbacks. Prefer the searchable pickers above.',
							'havenlytics'
						) }
					</p>

					<ToggleControl
						label={ __( 'Manual Property ID', 'havenlytics' ) }
						checked={ propertySource === 'manual' }
						onChange={ ( enabled ) =>
							setAttributes( {
								propertySource: enabled ? 'manual' : 'selected',
							} )
						}
						__nextHasNoMarginBottom
					/>
					{ propertySource === 'manual' && (
						<TextControl
							type="number"
							label={ __( 'Property ID', 'havenlytics' ) }
							value={ propertyId || '' }
							onChange={ ( value ) =>
								setAttributes( {
									propertyId: value ? parseInt( value, 10 ) : 0,
								} )
							}
							__nextHasNoMarginBottom
						/>
					) }

					<ToggleControl
						label={ __( 'Manual Agent ID', 'havenlytics' ) }
						checked={ agentSource === 'manual' }
						onChange={ ( enabled ) =>
							setAttributes( {
								agentSource: enabled ? 'manual' : 'auto',
							} )
						}
						__nextHasNoMarginBottom
					/>
					{ agentSource === 'manual' && (
						<TextControl
							type="number"
							label={ __( 'Agent ID', 'havenlytics' ) }
							value={ agentId || '' }
							onChange={ ( value ) =>
								setAttributes( {
									agentId: value ? parseInt( value, 10 ) : 0,
								} )
							}
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ missingProperty && (
					<Notice status="warning" isDismissible={ false }>
						{ propertySource === 'multiple'
							? __( 'Choose at least one property so visitors can select where to send their inquiry.', 'havenlytics' )
							: __( 'Choose a property for this inquiry form. The Havenlytics inquiry system requires a published property.', 'havenlytics' ) }
					</Notice>
				) }
				{ propertySource === 'multiple' && ids.length > 0 && (
					<Notice status="info" isDismissible={ false }>
						{ __( 'Multiple properties selected — the form will show a property dropdown with titles.', 'havenlytics' ) }
					</Notice>
				) }
				<BlockPreview
					block="havenlytics/property-inquiry"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
